import React, { useEffect, useState } from 'react'

/**
 * VerifyingToken component shown while verifying login token
 *
 * Automatically verifies the token from URL params and handles success/error states
 *
 * @param {string} token - The login token to verify
 * @param {Function} onSuccess - Callback called when verification succeeds (redirects by default)
 * @param {Function} onError - Callback called when verification fails
 */
const VerifyingToken = ({ token, onSuccess, onError }) => {
  const [status, setStatus] = useState('verifying') // 'verifying' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setStatus('error')
        setErrorMessage('Invalid token format')
        if (onError) onError('Invalid token format')
        return
      }

      try {
        // The verify endpoint handles the session and redirects, but we'll check the response
        const response = await fetch(`/auth/verify/${token}`, {
          method: 'GET',
          credentials: 'include',
          redirect: 'manual'
        })

        if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 0) {
          // Redirect happened - verification successful
          setStatus('success')
          if (onSuccess) {
            onSuccess()
          } else {
            // Default: redirect to home page
            window.location.href = '/'
          }
        } else if (response.ok) {
          // Also success but no redirect (shouldn't happen with current implementation)
          setStatus('success')
          if (onSuccess) {
            onSuccess()
          } else {
            window.location.href = '/'
          }
        } else {
          // Error response
          const data = await response.json()
          setStatus('error')
          setErrorMessage(data.error || 'Login link has expired or is invalid')
          if (onError) onError(data.error)
        }
      } catch (err) {
        setStatus('error')
        setErrorMessage('Network error. Please check your connection and try again.')
        if (onError) onError(err.message)
      }
    }

    verifyToken()
  }, [token, onSuccess, onError])

  if (status === 'verifying') {
    return (
      <div style={{
        maxWidth: '400px',
        margin: '100px auto',
        padding: '40px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        textAlign: 'center'
      }}>
        {/* Loading spinner */}
        <div style={{
          width: '60px',
          height: '60px',
          margin: '0 auto 24px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #667eea',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>

        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '12px',
          color: '#1a1a1a'
        }}>
          Verifying your login link...
        </h2>

        <p style={{
          fontSize: '14px',
          color: '#666',
          lineHeight: '1.6'
        }}>
          Please wait while we securely log you in
        </p>
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div style={{
        maxWidth: '400px',
        margin: '100px auto',
        padding: '40px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        textAlign: 'center'
      }}>
        {/* Success checkmark */}
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '12px',
          color: '#1a1a1a'
        }}>
          Login successful!
        </h2>

        <p style={{
          fontSize: '14px',
          color: '#666',
          lineHeight: '1.6'
        }}>
          Redirecting you now...
        </p>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div style={{
        maxWidth: '450px',
        margin: '100px auto',
        padding: '40px',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        textAlign: 'center'
      }}>
        {/* Error icon */}
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 24px',
          background: '#fee',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#c33"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>

        <h2 style={{
          fontSize: '24px',
          fontWeight: '600',
          marginBottom: '12px',
          color: '#1a1a1a'
        }}>
          Verification failed
        </h2>

        <p style={{
          fontSize: '16px',
          color: '#666',
          marginBottom: '24px',
          lineHeight: '1.6'
        }}>
          {errorMessage}
        </p>

        <div style={{
          padding: '16px',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          marginBottom: '24px',
          textAlign: 'left'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: 0,
            lineHeight: '1.6'
          }}>
            Please request a new login link. Links expire after 15 minutes and can only be used once.
          </p>
        </div>

        <a
          href="/auth/login"
          style={{
            display: 'inline-block',
            padding: '14px 28px',
            fontSize: '16px',
            fontWeight: '600',
            color: 'white',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            borderRadius: '8px',
            textDecoration: 'none',
            cursor: 'pointer',
            transition: 'transform 0.1s'
          }}
          onMouseDown={(e) => e.target.style.transform = 'scale(0.98)'}
          onMouseUp={(e) => e.target.style.transform = 'scale(1)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          Request new login link
        </a>
      </div>
    )
  }

  return null
}

export default VerifyingToken
